import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import dayjs from 'dayjs';

import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';

import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import TopSection from '../TopSection';
import { requestWithAuth } from '../../services/api';
import {
  aidOrganizationPlanList,
  staticFunctionFlags,
} from '../../utils/constants';
import { selectOptionFormatterFunctionFlag } from '../../utils/general';

interface RequestParams {
  page_size?: number;
  page?: number;
  sort?: string;
  aidorganization_id?: number;
  organization_name?: string;
  stytch_org_slug?: string;
  permanent_refcode?: string;
  user_sub_user_email?: string;
  user_sub_email?: string;
}

const searchKeys = [
  'organization_name',
  'stytch_org_slug',
  'permanent_refcode',
  'user_sub_user_email',
];

// Read-only fields of the subscription. Do not send them back on PATCH.
const READ_ONLY_FIELDS = ['user', 'maxio_customer_id', 'billing_portal_url'];

let params: RequestParams = {};
let paramsSecondary: RequestParams = {};

interface FormVals {
  id?: number;
  aidorganization_id?: number;
  organization_name?: string;
  plan?: string;
  created?: string;
  modified?: string;
  inactive_date?: string;
  is_active?: boolean;
  expiry_date?: string;
  plan_credits?: number;
  owner?: string;
  max_seats?: number;
  paid_seats?: number;
  maxio_renewal_event_count?: number;
  forced_to_premium?: boolean;
  pending_cancellation?: boolean | null;
  migrating_to_gtm?: boolean;
  last_annual_credit_renewal?: string | null;
  last_maxio_renewal_event?: string | null;
  // Member email for a user sub. Null for an org-level sub.
  user?: string | null;
  maxio_customer_id?: number | null;
  billing_portal_url?: string | null;
}

const SubscriptionScope = ({ user }: { user?: string | null }) =>
  user ? (
    <Tag
      color="blue"
      style={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        marginInlineEnd: 0,
      }}
    >
      User: {user}
    </Tag>
  ) : (
    <Tag>Organization</Tag>
  );

const FormItems = ({
  formVals,
  form,
  isUpdate,
  isNexisAdmin,
  accountReps,
  functionFlags,
}) => {
  useEffect(() => {
    form.resetFields();
  }, [formVals, form]);

  return (
    <>
      {isUpdate && (
        <>
          <Form.Item
            name="id"
            label="Aid Organization ID"
            initialValue={formVals.aidorganization_id}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="aid_id"
            label="Aid Organization Subscription ID"
            initialValue={formVals.id}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="name"
            label="Organization Name"
            initialValue={formVals.organization_name}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item label="Applies To">
            <SubscriptionScope user={formVals.user} />
          </Form.Item>
        </>
      )}
      <Form.Item
        name="plan"
        label="Plan"
        initialValue={formVals.plan}
        rules={
          isUpdate ? undefined : [{ required: true, message: 'Select Plan' }]
        }
      >
        {isUpdate ? (
          <Input disabled />
        ) : (
          <Select
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            placeholder="Plan"
            options={aidOrganizationPlanList}
          />
        )}
      </Form.Item>
      {isUpdate && (
        <>
          <Form.Item
            name="created"
            label="Created"
            initialValue={
              formVals.created ? dayjs(formVals.created) : undefined
            }
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" disabled />
          </Form.Item>
          <Form.Item
            name="modified"
            label="Modified"
            initialValue={
              formVals.modified ? dayjs(formVals.modified) : undefined
            }
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" disabled />
          </Form.Item>
          <Form.Item
            name="inactive_date"
            label="Inactive Date"
            initialValue={
              formVals.inactive_date ? dayjs(formVals.inactive_date) : undefined
            }
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" disabled />
          </Form.Item>
        </>
      )}
      <Form.Item
        name="is_active"
        label="Is active"
        initialValue={isUpdate ? formVals.is_active : true}
        valuePropName="checked"
      >
        <Checkbox style={{ lineHeight: '32px' }} />
      </Form.Item>
      <Form.Item
        name="expiry_date"
        label="Expiry Date"
        rules={[{ required: true, message: 'Select Expiry Date' }]}
        initialValue={
          formVals.expiry_date ? dayjs(formVals.expiry_date) : undefined
        }
      >
        <DatePicker format="YYYY-MM-DD" />
      </Form.Item>
      {!isNexisAdmin && (
        <Form.Item
          name="aidorg_subscription_function_flags"
          label={<div>Function Flags</div>}
          initialValue={formVals.aidorg_subscription_function_flags}
        >
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            options={functionFlags.map(selectOptionFormatterFunctionFlag)}
          />
        </Form.Item>
      )}
      <Form.Item
        name="plan_credits"
        label="Recurring Credits"
        initialValue={formVals.plan_credits}
      >
        <InputNumber style={{ width: '100%' }} placeholder="Credits" />
      </Form.Item>
      <Form.Item
        name="owner"
        label="Owner"
        required
        rules={[
          {
            required: true,
            message: 'Enter Owner',
          },
        ]}
        initialValue={formVals.owner}
      >
        <Select
          style={{ width: '100%' }}
          showSearch
          filterOption={(input, option) =>
            option?.children?.toString().includes(input)
          }
          filterSort={(optionA, optionB) =>
            (optionA?.children?.toString() || '')
              .toLowerCase()
              .localeCompare(
                (optionB?.children?.toString() || '').toLowerCase(),
              )
          }
        >
          {accountReps.map(({ value, text }) => (
            <Select.Option key={value} value={value}>
              {text}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="max_seats"
        label="Max Seats"
        initialValue={formVals.max_seats}
      >
        <InputNumber style={{ width: '100%' }} placeholder="Max Seats" />
      </Form.Item>
      {isUpdate && (
        <Form.Item
          name="paid_seats"
          label="Paid Seats"
          initialValue={formVals.paid_seats}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Paid Seats"
            readOnly
          />
        </Form.Item>
      )}
      {isUpdate && !isNexisAdmin && (
        <Form.Item
          name="maxio_renewal_event_count"
          label="Maxio Renewal Event Count"
          initialValue={formVals.maxio_renewal_event_count}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Maxio renewal event count"
          />
        </Form.Item>
      )}
      {isUpdate && !isNexisAdmin && (
        <>
          <Form.Item
            name="forced_to_premium"
            label="Forced to Premium"
            initialValue={formVals.forced_to_premium ?? false}
            valuePropName="checked"
          >
            <Checkbox disabled />
          </Form.Item>
          <Form.Item
            name="pending_cancellation"
            label="Pending Cancellation"
            initialValue={formVals.pending_cancellation ?? false}
            valuePropName="checked"
          >
            <Checkbox disabled />
          </Form.Item>
          <Form.Item
            name="migrating_to_gtm"
            label="Migrating to GTM"
            initialValue={formVals.migrating_to_gtm ?? false}
            valuePropName="checked"
          >
            <Checkbox disabled />
          </Form.Item>
        </>
      )}
      {isUpdate && !isNexisAdmin && (
        <>
          <Form.Item
            name="last_annual_credit_renewal"
            label="Last Annual Credit Renewal"
            initialValue={
              formVals.last_annual_credit_renewal
                ? dayjs(formVals.last_annual_credit_renewal)
                : undefined
            }
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" disabled />
          </Form.Item>
          <Form.Item
            name="last_maxio_renewal_event"
            label="Last Maxio Renewal Event"
            initialValue={
              formVals.last_maxio_renewal_event
                ? dayjs(formVals.last_maxio_renewal_event)
                : undefined
            }
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" disabled />
          </Form.Item>
          <Form.Item
            name="maxio_customer_id"
            label="Maxio Customer ID"
            initialValue={formVals.maxio_customer_id}
          >
            <Input placeholder="N/A" disabled />
          </Form.Item>
          <Form.Item
            name="billing_portal_url"
            label="Billing Portal URL"
            initialValue={formVals.billing_portal_url}
          >
            <Input placeholder="N/A" disabled />
          </Form.Item>
        </>
      )}
    </>
  );
};

const FormModal = ({
  form,
  isUpdate,
  isNexisAdmin,
  handleOrgSubscriptionUpdate,
  handleOrgSubscriptionCreate,
  isModalLoading,
  onClose,
  recordFormVals,
  accountReps,
  error,
  functionFlags,
}) => {
  const [formVals, setFormVals] = useState<FormVals>({});

  useEffect(() => {
    if (recordFormVals) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormVals({ ...recordFormVals });
    }
  }, [recordFormVals]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const formValues = { ...values };
        if (formValues.expiry_date) {
          formValues.expiry_date = formValues.expiry_date.format('YYYY-MM-DD');
        }
        if (isUpdate) {
          READ_ONLY_FIELDS.forEach((field) => delete formValues[field]);
          handleOrgSubscriptionUpdate(formVals.id, formValues);
        } else {
          handleOrgSubscriptionCreate({
            aidorganization_id: formVals.aidorganization_id,
            owner: values.owner,
            plan: values.plan,
            plan_credits: values.plan_credits,
            max_seats: values.max_seats,
            aidorg_subscription_function_flags:
              values.aidorg_subscription_function_flags,
            is_active: values.is_active ?? true,
            expiry_date: formValues.expiry_date,
          });
        }
      })
      .catch((errorInfo) => {
        if (errorInfo) {
          console.log(errorInfo);
          message.error('Please fill the mandatory fileds');
          return;
        }
      });
  };

  return (
    <Modal
      title={
        isUpdate
          ? 'Edit Aid Organization Subscription'
          : 'New Aid Organization Subscription'
      }
      open
      width={1000}
      okText={isUpdate ? 'Update' : 'Create'}
      onOk={handleSubmit}
      confirmLoading={isModalLoading}
      onCancel={onClose}
    >
      <Spin size="default" spinning={isModalLoading}>
        <Form labelCol={{ span: 7 }} wrapperCol={{ span: 13 }} form={form}>
          <FormItems
            formVals={formVals}
            form={form}
            isUpdate={isUpdate}
            isNexisAdmin={isNexisAdmin}
            accountReps={accountReps}
            functionFlags={functionFlags}
          />
          {error && <div className="error-text">{error}</div>}
        </Form>
      </Spin>
    </Modal>
  );
};

const AidOrganizationSubscription = ({ isNexisAdmin }) => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [orgData, setOrgData] = useState([]);
  const [orgDataCount, setOrgDataCount] = useState(0);
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [searchQuery, setSearchQuery] = useState(
    () => searchKeys.map((key) => searchParams.get(key)).find(Boolean) ?? '',
  );
  const [expandedOrgKey, setExpandedOrgKey] = useState(null);
  const [sorterPrimary, setSorterPrimary] = useState([]);
  const [sorterSecondary, setSorterSecondary] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [recordFormVals, setRecordFormVals] = useState(null);
  const [error, setError] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [accountReps, setAccountReps] = useState([]);
  const [functionFlags] = useState([...staticFunctionFlags]);
  const [searchType, setSearchType] = useState(
    () =>
      searchKeys.find((key) => searchParams.get(key)) ?? 'organization_name',
  );
  // Emails are stored lowercase and the API does a prefix match on them.
  const userEmailQuery =
    searchType === 'user_sub_user_email'
      ? searchQuery.trim().toLowerCase()
      : '';

  const [listPagination, setListPagination] = useState({
    total: 0,
    pageSize: 50,
    current: 1,
  });
  const [secondListPagination, setSecondListPagination] = useState({
    total: 0,
    pageSize: 50,
    current: 1,
  });

  const { pageSize, current } = listPagination;
  const [form] = Form.useForm();

  const orgSourceRef = useRef<
    { cancel: (message?: string) => void; token: unknown } | undefined
  >(undefined);
  const subscriptionSourceRef = useRef<
    { cancel: (message?: string) => void; token: unknown } | undefined
  >(undefined);

  const paginationProps = {
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ['20', '50', '100', '200'],
    defaultPageSize: pageSize,
    ...listPagination,
  };

  const secondPaginationProps = {
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ['20', '50', '100', '200'],
    defaultPageSize: secondListPagination.pageSize,
    ...secondListPagination,
  };

  // The effect below fetches the subscriptions when the expanded key or the
  // secondary sorter changes.
  const expandOrg = useCallback((aidOrgId: number) => {
    paramsSecondary = {};
    setSorterSecondary([]);
    setSecondListPagination({
      current: 1,
      pageSize: 50,
      total: 0,
    });
    setExpandedOrgKey(aidOrgId);
  }, []);

  const fetchOrgData = useCallback(() => {
    const newParams: RequestParams = {
      ...params,
      page_size: pageSize,
      page: current,
    };
    if (userEmailQuery) newParams.user_sub_user_email = userEmailQuery;
    else if (searchQuery && searchType !== 'user_sub_user_email')
      newParams[searchType] = searchQuery;

    if (orgSourceRef.current)
      orgSourceRef.current.cancel('Operation canceled by the user.');
    const { CancelToken } = axios; // eslint-disable-line import-x/no-named-as-default-member
    const source = CancelToken.source();
    orgSourceRef.current = source;

    requestWithAuth(
      'vault-aid-organization',
      'GET',
      newParams,
      null,
      source.token,
    ).then((response) => {
      // A newer fetch replaced this one; let it manage the state.
      if (orgSourceRef.current !== source) return;
      if (response && response.results) {
        setOrgData(response.results);
        setOrgDataCount(response.count);
        if (response.results.length === 1) {
          expandOrg(response.results[0].id);
        } else {
          setExpandedOrgKey((prevKey) =>
            response.results.some((org) => org.id === prevKey) ? prevKey : null,
          );
        }
        setListPagination((prevState) => ({
          ...prevState,
          total: response.count,
        }));
      }
      setIsLoading(false);
    });
  }, [pageSize, current, searchQuery, searchType, userEmailQuery, expandOrg]);

  const fetchOrgSubscription = useCallback(
    (aidOrgid: number) => {
      const newParams: RequestParams = {
        ...paramsSecondary,
        page_size: secondListPagination.pageSize,
        page: secondListPagination.current,
        aidorganization_id: aidOrgid,
      };
      // Show only the searched user's subscriptions under the org.
      if (userEmailQuery) newParams.user_sub_email = userEmailQuery;

      if (subscriptionSourceRef.current)
        subscriptionSourceRef.current.cancel('Operation canceled by the user.');
      const { CancelToken } = axios; // eslint-disable-line import-x/no-named-as-default-member
      const source = CancelToken.source();
      subscriptionSourceRef.current = source;
      // Block the table until the rows are current, so Edit can't open the
      // modal with a row from before the last save.
      setIsSubscriptionLoading(true);

      requestWithAuth(
        'vault-aid-organization-subscription',
        'GET',
        newParams,
        null,
        source.token,
      ).then((response) => {
        // A newer fetch replaced this one; let it manage the state.
        if (subscriptionSourceRef.current !== source) return;
        if (response && response.results) {
          setSubscriptionData(response.results);
          setSecondListPagination((prevState) => ({
            ...prevState,
            total: response.count,
          }));
          setIsLoading(false);
        }
        setIsSubscriptionLoading(false);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- using .pageSize/.current avoids infinite loop from object reference changes
    [
      secondListPagination.pageSize,
      secondListPagination.current,
      userEmailQuery,
    ],
  );

  useEffect(() => {
    fetchOrgData();
  }, [fetchOrgData, sorterPrimary]);

  useEffect(() => {
    if (expandedOrgKey) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchOrgSubscription(expandedOrgKey);
    }
  }, [fetchOrgSubscription, sorterSecondary, expandedOrgKey]);

  const fetchAccountReps = () => {
    requestWithAuth('admin-field-lookup', 'GET', { key: 'account_rep' }, null)
      .then((response) => {
        if (response && response.result && response.result.length) {
          const accountReps = [];
          response.result.map((rep) => {
            accountReps.push({ value: rep.email, text: rep.email });
            return '';
          });
          accountReps.sort();
          setAccountReps(accountReps);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    fetchAccountReps();
    return () => {
      params = {};
      paramsSecondary = {};
    };
  }, []);

  const handleOrgSubscriptionUpdate = (subscriptionId, fields) => {
    setIsModalLoading(true);
    setIsLoading(true);
    requestWithAuth(
      `vault-aid-organization-subscription/${subscriptionId}`,
      'PATCH',
      {},
      fields,
    ).then((response) => {
      if (response && !response.error) {
        message.success('Subscription updated successfully');
        fetchOrgSubscription(expandedOrgKey);
        setShowFormModal(false);
        setIsLoading(false);
        setIsModalLoading(false);
      } else if (response?.error) {
        setError(Object.values(response.error).join(', '));
        setIsLoading(false);
        setIsModalLoading(false);
      } else {
        setIsLoading(false);
        setIsModalLoading(false);
      }
    });
  };

  const handleOrgSubscriptionCreate = (fields) => {
    setIsModalLoading(true);
    setIsLoading(true);
    requestWithAuth(
      'vault-aid-organization-subscription',
      'POST',
      null,
      fields,
    ).then((response) => {
      if (response && !response.error && response.id) {
        message.success('Subscription created successfully');
        fetchOrgSubscription(expandedOrgKey);
        setShowFormModal(false);
        setIsLoading(false);
        setIsModalLoading(false);
      } else if (response?.error) {
        setError(Object.values(response.error).join(', '));
        setIsLoading(false);
        setIsModalLoading(false);
      } else {
        setIsLoading(false);
        setIsModalLoading(false);
      }
    });
  };

  const handleDelete = (id) => {
    setIsLoading(true);
    requestWithAuth(
      `vault-aid-organization-subscription/${id}`,
      'DELETE',
      null,
      null,
    ).then((response) => {
      if (response?.error) {
        message.error(Object.values(response.error).join(', '));
      } else if (response !== undefined) {
        // DELETE returns 204 No Content → response is '' (falsy but defined).
        // Only `undefined` signals a swallowed failure (already toasted).
        message.success('Subscription deleted');
        fetchOrgSubscription(expandedOrgKey);
      }
      setIsLoading(false);
    });
  };

  const handleSearchQuery = (searchValue: string) => {
    setSearchQuery(searchValue || '');
    setListPagination((prevState) => ({
      ...prevState,
      current: 1,
    }));
  };

  const handleSearchTypeChange = (value: string) => {
    setSearchType(value);
    setSearchQuery('');
    setListPagination((prevState) => ({
      ...prevState,
      current: 1,
    }));
    params = {};
  };

  const handleTableChange = (pagination, filtersArg, sorter) => {
    sorter = Array.isArray(sorter) ? sorter : [sorter];
    setSorterPrimary(sorter);
    // Remove all items from sorter that don't have an order
    sorter = sorter.filter((item) => item.order);
    if (sorter.length > 0) {
      params.sort = sorter
        .map((info) =>
          info.order === 'descend' ? `-${info.columnKey}` : info.columnKey,
        )
        .join(',');
    } else {
      delete params.sort;
    }

    setListPagination({
      ...pagination,
      pageSize: pagination.pageSize,
      current: pagination.current,
    });
  };

  const handleSecondaryTableChange = (pagination, filtersArg, sorter) => {
    sorter = Array.isArray(sorter) ? sorter : [sorter];
    setSorterSecondary(sorter);
    // Remove all items from sorter that don't have an order
    sorter = sorter.filter((item) => item.order);
    if (sorter.length > 0) {
      paramsSecondary.sort = sorter
        .map((info) =>
          info.order === 'descend' ? `-${info.columnKey}` : info.columnKey,
        )
        .join(',');
    } else {
      delete paramsSecondary.sort;
    }
    setSecondListPagination({
      ...pagination,
      pageSize: pagination.pageSize,
      current: pagination.current,
    });
  };

  const handleFormReset = () => {
    params = {};
    setOrgData([]);
    setOrgDataCount(0);
    setIsLoading(true);
    setSorterPrimary([]);

    setListPagination({
      current: 1,
      pageSize: 50,
      total: 0,
    });

    setSearchQuery('');
  };

  const showUpdateForm = (subscriptionRecord) => {
    const aidOrgData = orgData.find(
      (org) => org.id === subscriptionRecord.aidorganization_id,
    );

    setIsUpdate(true);
    setShowFormModal(true);
    setRecordFormVals({
      id: aidOrgData?.id,
      organization_name: aidOrgData?.organization_name,
      ...subscriptionRecord,
    });
  };

  const showCreateForm = (aidOrg) => {
    setIsUpdate(false);
    setShowFormModal(true);
    setRecordFormVals({ aidorganization_id: aidOrg.id });
  };

  const primaryColumns: object[] = [
    {
      title: 'Organization',
      key: 'organization_name',
      dataIndex: 'organization_name',
      width: 400,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterPrimary.find(
        (info) => info.columnKey === 'organization_name',
      )?.order,
    },
    {
      title: 'Organization Slug',
      key: 'stytch_org_slug',
      dataIndex: 'stytch_org_slug',
      width: 400,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterPrimary.find(
        (info) => info.columnKey === 'stytch_org_slug',
      )?.order,
    },
    {
      title: 'Permanent Ref Code',
      key: 'permanent_refcode',
      dataIndex: 'permanent_refcode',
      width: 400,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterPrimary.find(
        (info) => info.columnKey === 'permanent_refcode',
      )?.order,
    },
    {
      title: 'Total Users',
      key: 'total_user_count',
      dataIndex: 'total_user_count',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterPrimary.find(
        (info) => info.columnKey === 'total_user_count',
      )?.order,
      render: (val, record) => (
        <Link to={`/user-editor/users?aidorganization_id=${record.id}`}>
          {val || 0}
        </Link>
      ),
    },
  ];

  const secondaryColumns: object[] = [
    {
      title: (
        <Space size={4}>
          Applies To
          <Tooltip title="An organization subscription applies to all members. A user subscription applies only to that member and overrides the organization subscription.">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'user',
      key: 'user',
      width: 160,
      render: (val) => <SubscriptionScope user={val} />,
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      width: 160,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterSecondary.find((info) => info.columnKey === 'plan')
        ?.order,
    },
    {
      title: 'Is Active',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 110,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterSecondary.find((info) => info.columnKey === 'is_active')
        ?.order,
      render: (text, record) => (record.is_active ? 'Yes' : 'No'),
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterSecondary.find((info) => info.columnKey === 'created')
        ?.order,
      render: (val) => {
        return (
          <div
            style={{ width: '100%', wordBreak: 'break-word', display: 'block' }}
          >
            {val ? dayjs(val).format('MM/DD/YYYY') : ''}
          </div>
        );
      },
    },
    {
      title: 'Expiration Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterSecondary.find(
        (info) => info.columnKey === 'expiry_date',
      )?.order,
      render: (val) => {
        return (
          <div
            style={{ width: '100%', wordBreak: 'break-word', display: 'block' }}
          >
            {val ? dayjs(val).format('MM/DD/YYYY') : ''}
          </div>
        );
      },
    },
    {
      title: (
        <Space size={4}>
          Max Seats
          <Tooltip title="Upper limit of seats if invoice, upper purchasable limit if paywall. A hyphen/blank falls back to the plan's default limit.">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'max_seats',
      key: 'max_seats',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterSecondary.find((info) => info.columnKey === 'max_seats')
        ?.order,
      render: (val) => (val === null || val === undefined ? '-' : val),
    },
    {
      title: (
        <Space size={4}>
          Paid Seats
          <Tooltip title="Currently purchased seats for paywall users.">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'paid_seats',
      key: 'paid_seats',
      width: 120,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sorterSecondary.find((info) => info.columnKey === 'paid_seats')
        ?.order,
      render: (val) => (val === null || val === undefined ? '-' : val),
    },
    ...(isNexisAdmin
      ? []
      : [
          {
            title: 'Maxio Customer ID',
            dataIndex: 'maxio_customer_id',
            key: 'maxio_customer_id',
            width: 140,
            render: (val) => (val === null || val === undefined ? '-' : val),
          },
        ]),
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 120,
      onCell: () => ({
        onClick: (e) => {
          e.stopPropagation();
        },
      }),
      render: (text, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => showUpdateForm(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const secondaryTableRender = (row) => {
    if (expandedOrgKey === row.id) {
      return (
        <>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ marginBottom: 12 }}
            onClick={() => showCreateForm(row)}
          >
            New Subscription
          </Button>
          <Table
            columns={secondaryColumns}
            dataSource={subscriptionData}
            loading={isSubscriptionLoading}
            onChange={handleSecondaryTableChange}
            pagination={secondPaginationProps}
            scroll={{ x: 'max-content' }}
            style={{ width: 'calc(100vw - 360px)' }}
          />
        </>
      );
    }
  };

  return (
    <>
      <div className="aid-organization-subscription">
        <TopSection
          placeholder="Search Organization"
          count={orgDataCount}
          handleSearchQuery={handleSearchQuery}
          handleFormReset={handleFormReset}
          showSearch
          showNewButton={false}
          searchValue={searchQuery}
          searchPrefix={
            <Select
              value={searchType}
              onChange={handleSearchTypeChange}
              style={{ width: 180 }}
            >
              <Select.Option value="organization_name">Org Name</Select.Option>
              <Select.Option value="stytch_org_slug">Org Slug</Select.Option>
              <Select.Option value="permanent_refcode">Ref Code</Select.Option>
              <Select.Option value="user_sub_user_email">
                User Email
              </Select.Option>
            </Select>
          }
        />
        <Spin size="default" spinning={isLoading}>
          <Table
            rowKey="id"
            className="aid-table-nested"
            columns={primaryColumns}
            dataSource={orgData}
            expandable={{
              expandedRowRender: (row) => secondaryTableRender(row),
              expandedRowKeys: expandedOrgKey ? [expandedOrgKey] : [],
              defaultExpandedRowKeys: [],
              onExpand: (expanded, record) => {
                if (expanded) {
                  expandOrg(record.id);
                } else {
                  setExpandedOrgKey(null);
                }
              },
            }}
            onChange={handleTableChange}
            pagination={paginationProps}
          />
        </Spin>
      </div>
      {showFormModal && (
        <FormModal
          form={form}
          isUpdate={isUpdate}
          isNexisAdmin={isNexisAdmin}
          handleOrgSubscriptionUpdate={handleOrgSubscriptionUpdate}
          handleOrgSubscriptionCreate={handleOrgSubscriptionCreate}
          isModalLoading={isModalLoading}
          recordFormVals={recordFormVals}
          accountReps={accountReps}
          error={error}
          functionFlags={functionFlags}
          onClose={() => {
            form.resetFields();
            setIsUpdate(false);
            setShowFormModal(false);
            setRecordFormVals(null);
            setError(null);
          }}
        />
      )}
    </>
  );
};

export default AidOrganizationSubscription;
